// src/components/dnl/MappingArrowsLayer.tsx
/**
 * Mapping Arrows Layer Component
 * Phase 4: Mapping Arrows Feature
 * 
 * Renders arrows connecting corresponding points on primary and secondary lines.
 * Supports different arrow styles (solid, dashed, dotted) and optional multiplier labels.
 * 
 * Based on DNL Enhancement Specification v1.0, Phase 4
 */

import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../types/redux';
import type { MappingArrowsConfig, DNLCustomTick } from '../../types/dnl';
import type { Fraction } from '../../types/redux';
import { valueToPx } from '../../lib/scale';

// =============================================================================
// TYPES
// =============================================================================

export interface MappingArrowsLayerProps {
  /** Custom ticks to use for arrow generation */
  customTicks: DNLCustomTick[];
  /** Mapping arrows configuration */
  arrowsConfig: MappingArrowsConfig;
  /** Whether the number line is horizontal */
  isHorizontal: boolean;
  /** Primary line Y position (for horizontal) or X position (for vertical) */
  primaryLineY: number;
  /** Secondary line Y position (for horizontal) or X position (for vertical) */
  secondaryLineY: number;
  /** Domain range */
  domain: { start: number; end: number };
  /** Length of the line in pixels */
  lineLength: number;
  /** Scale factor between primary and secondary lines */
  scaleFactor: Fraction;
  /** Dark mode */
  isDark?: boolean;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const ARROW_HEAD_SIZE = 12;
const ARROW_STROKE_WIDTH = 2;
const LABEL_OFFSET = 8;
const LABEL_FONT_SIZE = 11;

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get stroke-dasharray value for arrow style
 */
function getDashArray(style: 'solid' | 'dashed' | 'dotted'): string {
  switch (style) {
    case 'solid':
      return 'none';
    case 'dashed':
      return '8,4';
    case 'dotted':
      return '2,3';
    default:
      return 'none';
  }
}

/**
 * Format multiplier label (×k)
 */
function formatMultiplierLabel(scaleFactor: Fraction): string {
  const factor = scaleFactor.n / scaleFactor.d;
  
  // If it's a simple fraction, show it as a fraction
  if (scaleFactor.d !== 1 && scaleFactor.n !== 1) {
    return `×${scaleFactor.n}/${scaleFactor.d}`;
  }
  
  // Otherwise show as decimal if not a whole number
  if (factor === Math.floor(factor)) {
    return `×${factor}`;
  }
  
  // Round to 2 decimal places for display
  return `×${factor.toFixed(2)}`;
}

/**
 * Create arrowhead marker path
 * Path is centered at origin (0,0) with tip pointing right
 * The geometric center of the triangle is at (0,0)
 */
function getArrowheadPath(): string {
  // Arrowhead pointing right, centered at origin
  // For a triangle with center at (0,0), if tip is at (t, 0), base should be at (-t/2, ...)
  // Tip at (ARROW_HEAD_SIZE/2, 0), base at (-ARROW_HEAD_SIZE/4, ...)
  const halfSize = ARROW_HEAD_SIZE / 2;
  const baseX = -ARROW_HEAD_SIZE / 4; // Base positioned so triangle center is at (0,0)
  return `M ${halfSize},0 L ${baseX},-${halfSize} L ${baseX},${halfSize} Z`;
}

// =============================================================================
// ARROW COMPONENT
// =============================================================================

interface ArrowProps {
  arrow: {
    id: string;
    value: number;
    visible: boolean;
    style: 'solid' | 'dashed' | 'dotted';
    showArrowHeads: 'none' | 'top' | 'bottom' | 'both';
    showMultiplierLabel: boolean;
    colour: string;
  };
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  isHorizontal: boolean;
  scaleFactor: Fraction;
  isDark: boolean;
}

const Arrow: React.FC<ArrowProps> = ({
  arrow,
  x1,
  y1,
  x2,
  y2,
  isHorizontal,
  scaleFactor,
  isDark,
}) => {
  if (!arrow.visible) {
    return null;
  }

  const dashArray = getDashArray(arrow.style);
  const midX = (x1 + x2) / 2;
  const midY = (y1 + y2) / 2;
  
  // Calculate arrow direction angle
  // For horizontal lines: arrow goes up (from primary to secondary), angle = -90 degrees
  // For vertical lines: arrow goes right (from primary to secondary), angle = 0 degrees
  const angle = isHorizontal ? -90 : 0;
  
  // Only show arrowhead if configured to show
  const showArrowhead = arrow.showArrowHeads !== 'none';
  
  return (
    <g className="mapping-arrow">
      {/* Arrow line (no markers) */}
      <line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke={arrow.colour}
        strokeWidth={ARROW_STROKE_WIDTH}
        strokeDasharray={dashArray}
        opacity={0.8}
      />
      
      {/* Single arrowhead at midpoint */}
      {showArrowhead && (
        <g transform={`translate(${midX}, ${midY}) rotate(${angle})`}>
          <path
            d={getArrowheadPath()}
            fill={arrow.colour}
            stroke="none"
            opacity={0.8}
          />
        </g>
      )}
      
      {/* Multiplier label */}
      {arrow.showMultiplierLabel && (
        <text
          x={isHorizontal ? midX : midX + LABEL_OFFSET}
          y={isHorizontal ? midY - LABEL_OFFSET : midY}
          textAnchor="middle"
          dominantBaseline="central"
          fill={isDark ? '#E5E7EB' : '#374151'}
          fontSize={LABEL_FONT_SIZE}
          fontWeight={500}
          fontFamily="system-ui, -apple-system, sans-serif"
          className="arrow-multiplier-label"
        >
          {formatMultiplierLabel(scaleFactor)}
        </text>
      )}
    </g>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const MappingArrowsLayer: React.FC<MappingArrowsLayerProps> = ({
  customTicks,
  arrowsConfig,
  isHorizontal,
  primaryLineY,
  secondaryLineY,
  domain,
  lineLength,
  scaleFactor,
  isDark = false,
}) => {
  // Get theme from Redux if not provided
  const themeIsDark = useSelector((state: RootState) => state.preferences.theme === 'dark');
  const effectiveIsDark = isDark ?? themeIsDark;

  // Calculate arrow positions
  const arrows = useMemo(() => {
    if (!arrowsConfig.enabled || arrowsConfig.arrows.length === 0) {
      return [];
    }

    return arrowsConfig.arrows
      .filter(arrow => arrow.visible)
      .map(arrow => {
        // Calculate pixel position on primary line
        const primaryPx = valueToPx(arrow.value, domain, lineLength);
        
        // For horizontal lines, arrows are vertical (connect Y positions)
        // For vertical lines, arrows are horizontal (connect X positions)
        if (isHorizontal) {
          return {
            arrow,
            x1: primaryPx,
            y1: primaryLineY,
            x2: primaryPx,
            y2: secondaryLineY,
          };
        } else {
          return {
            arrow,
            x1: primaryLineY,
            y1: primaryPx,
            x2: secondaryLineY,
            y2: primaryPx,
          };
        }
      })
      .filter(arrowData => {
        // Filter out arrows that are outside the visible domain
        const px = isHorizontal ? arrowData.x1 : arrowData.y1;
        return px >= -50 && px <= lineLength + 50;
      });
  }, [
    arrowsConfig.enabled,
    arrowsConfig.arrows,
    isHorizontal,
    primaryLineY,
    secondaryLineY,
    domain,
    lineLength,
  ]);

  if (!arrowsConfig.enabled || arrows.length === 0) {
    return null;
  }

  return (
    <g className="mapping-arrows-layer">
      {arrows.map(({ arrow, x1, y1, x2, y2 }) => (
        <Arrow
          key={arrow.id}
          arrow={arrow}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          isHorizontal={isHorizontal}
          scaleFactor={scaleFactor}
          isDark={effectiveIsDark}
        />
      ))}
    </g>
  );
};

export default MappingArrowsLayer;

